/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo } from 'react';
import { useGetClassesOfRiskQuery } from '@/features/classesOfRisk/classesOfRiskApi';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { Skeleton, Typography, Stack, Checkbox } from '@mui/material';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { useTreeViewApiRef } from '@mui/x-tree-view';
import { useRiskClassSelectContext } from '@/context/riskClassSelectContext';
import Fuse from 'fuse.js';

const FUSE_OPTIONS = {
	keys: ['classOfRisk', 'label', 'parent'],
	threshold: 0.2,
};

const RiskClassTreeItem = ({ item, disabled }) => {
	const apiRef = useTreeViewApiRef();

	const { selectedItems, setSelectedItems, searchValue } = useRiskClassSelectContext();
	const classesOfRiskKeys = selectedItems.map((i) => i.classOfRisk);

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		}
	);

	const countryCode = userDetails?.broker?.country ?? 'NZ';
	const brokerGroupIds = userDetails?.brokerGroupArray;

	const { data: classesOfRisk, ...classesOfRiskQuery } = useGetClassesOfRiskQuery(
		{ countryCode, classOfRisk: item.classOfRisk, brokerGroupIds },
		{ skip: userDetails.isLoading || userDetails.isUninitialized || !item.isParent }
	);

	const sortedClassesOfRisk = useMemo(() => {
		if (!classesOfRisk) return [];

		const classOfRiskKeys = Object.fromEntries(classesOfRisk.map((i) => [i.classOfRisk, i]));

		const dependencyGroups = [];
		const usedKeys = new Set();

		classesOfRisk.forEach((item) => {
			const conditions = item.conditions;
			if (!conditions) return;

			const dependencies = [...(conditions.and ?? []), ...(conditions.or ?? [])];
			if (dependencies.length === 0) return;

			const bases = dependencies.map((key) => classOfRiskKeys[key]).filter(Boolean);

			const group = [...bases, item];
			group.forEach((i) => usedKeys.add(i.classOfRisk));
			dependencyGroups.push(group);
		});

		const ordered = Array.from(new Set(dependencyGroups.flat()));
		const remaining = classesOfRisk
			.filter((i) => !usedKeys.has(i.classOfRisk))
			.sort((a, b) => a.label.localeCompare(b.label));

		return [...ordered, ...remaining];
	}, [classesOfRisk]);

	const classOfRiskIndex = useMemo(() => {
		if (sortedClassesOfRisk.length > 0) {
			const fuseIndex = Fuse.createIndex(FUSE_OPTIONS.keys, sortedClassesOfRisk);
			return new Fuse(sortedClassesOfRisk, FUSE_OPTIONS, fuseIndex);
		}
	}, [sortedClassesOfRisk]);

	const searchedPages = useMemo(() => {
		return (classOfRiskIndex?.search(searchValue) ?? []).flatMap((v) => v?.item);
	}, [classOfRiskIndex, searchValue]);

	const viewableClassesOfRisk =
		searchValue && item.isParent ? searchedPages : sortedClassesOfRisk;

	const conditions = item.conditions ?? {};
	const andConditions = conditions.and ?? [];
	const orConditions = conditions.or ?? [];

	const hasAllAndConditions =
		andConditions.length === 0 || andConditions.every((c) => classesOfRiskKeys.includes(c));

	const hasAnyOrCondition =
		orConditions.length === 0 || orConditions.some((c) => classesOfRiskKeys.includes(c));

	const isDisabled = disabled || item.isParent || !hasAllAndConditions || !hasAnyOrCondition;

	const checked = useMemo(
		() => selectedItems.findIndex((i) => i.id === item.id) > -1,
		[selectedItems]
	);

	const handleChange = (e) => {
		apiRef?.current?.focusItem(e, checked ? null : item.id);
		const newItems = checked
			? selectedItems.filter((i) => i.id !== item.id)
			: [...selectedItems, item];
		setSelectedItems(newItems);
	};

	return (
		<TreeItem
			itemId={item.id}
			onClick={(e) => {
				if (!isDisabled) {
					handleChange(e);
				}
			}}
			label={
				<Stack direction='row' alignItems={'center'}>
					{!item.isParent && (
						<Checkbox
							size={'small'}
							checked={checked}
							onChange={handleChange}
							disabled={isDisabled}
							sx={{ p: '4px' }}
						/>
					)}
					<Typography {...(checked && { sx: { fontWeight: 'bold' } })}>
						{item.label}
					</Typography>
				</Stack>
			}
		>
			{classesOfRiskQuery.isLoading ? (
				<Skeleton />
			) : viewableClassesOfRisk.length > 0 ? (
				viewableClassesOfRisk.map((f, i) => (
					<RiskClassTreeItem
						key={`tree-item-child-${item.id}-${i}`}
						disabled={disabled}
						item={f}
					/>
				))
			) : null}
		</TreeItem>
	);
};

export default RiskClassTreeItem;
